import React from 'react';
import Editor from '@monaco-editor/react';
import clsx from 'clsx';
import * as monacoEditor from 'monaco-editor/esm/vs/editor/editor.api';
import { EditorConfiguration } from './EditorConfiguration';

import styles from './BrightScriptEditor.css';

type BrightScriptEditorProps = {
    parameterData: UseGetParametersResponse;
    domReadOnly?: boolean;
    editorValue: string | undefined;
    handleEditorChange: (value: string | undefined) => void;
    intellisenseData: IntellisenseSchema | undefined;
    listData?: ListManagementApiList[];
    readOnly?: boolean;
};

type Monaco = typeof monacoEditor;
type Model = monacoEditor.editor.ITextModel;
type Position = monacoEditor.IPosition;
type Range = monacoEditor.IRange;
type Disposable = monacoEditor.IDisposable;
type CompletionItem = monacoEditor.languages.CompletionItem;

const LANGUAGE = 'brightScript';

const BrightScriptEditor = ({
    parameterData,
    domReadOnly,
    editorValue,
    handleEditorChange,
    intellisenseData,
    listData,
    readOnly
}: BrightScriptEditorProps) => {
    const [completionDisposable, setCompletionDisposable] = React.useState<Disposable>();

    React.useEffect(() => {
        return () => {
            completionDisposable?.dispose();
        };
    }, [completionDisposable]);

    const getFieldsAndFunctions = (range: Range) => {
        const result: CompletionItem[] = [];

        listData?.forEach(({ name }) => {
            result.push({
                label: name,
                kind: 4,
                insertText: `${name}`,
                range
            });
        });

        const modelFields = Object.keys(parameterData.fieldNameHashmap);
        modelFields.forEach((field) => {
            result.push({
                label: field.toLowerCase(),
                kind: 1,
                insertText: `${field.toLowerCase()}`,
                range
            });
        });

        if (!intellisenseData) {
            return result;
        }

        // Intellisense function names
        const { functionNames, keywords } = intellisenseData;

        functionNames.forEach((label) => {
            let insertText = `${label}(\${1:condition});`;

            if (['isInList'].includes(label)) {
                insertText = `${label}("\${1:fieldName}", "\${2:listName}")`;
            }
            if (['addToList'].includes(label)) {
                insertText = `${label}("\${1:fieldName}", "\${2:listName}");`;
            }
            if (['setAccept', 'setReject', 'setReview', 'addToQueue', 'verifyOtp'].includes(label)) {
                insertText = `${label}();`;
            }

            result.push({
                label,
                kind: 1,
                insertText,
                range,
                insertTextRules: 4
            });
        });

        // Intellisense keywords
        keywords.forEach(({ label, snippet }) => {
            result.push({
                label,
                kind: 17,
                insertText: snippet,
                range,
                insertTextRules: 4
            });
        });

        return result;
    };

    const handleBeforeMount = (monaco: Monaco) => {
        monaco?.languages.register({ id: LANGUAGE });
        monaco?.languages.setMonarchTokensProvider(LANGUAGE, EditorConfiguration.definition());
        const disposeMethod = monaco?.languages.registerCompletionItemProvider(LANGUAGE, {
            provideCompletionItems: (model: Model, position: Position) => {
                const word = model.getWordUntilPosition(position);
                const range: Range = {
                    startLineNumber: position.lineNumber,
                    endLineNumber: position.lineNumber,
                    startColumn: word.startColumn,
                    endColumn: word.endColumn
                };
                return {
                    suggestions: getFieldsAndFunctions(range)
                };
            }
        });
        setCompletionDisposable(disposeMethod);
    };

    return (
        <Editor
            height='500px'
            className={clsx([styles.editor, readOnly && styles.readOnly])}
            defaultValue={editorValue}
            defaultLanguage={LANGUAGE}
            onChange={handleEditorChange}
            options={{
                readOnly,
                domReadOnly,
                minimap: {
                    enabled: false
                }
            }}
            beforeMount={handleBeforeMount}
            keepCurrentModel={false}
        />
    );
};

export default React.memo(BrightScriptEditor);
